import React from "react";
import { getElakayLanguageContext } from "./dataElakay";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["priProductTable"]?.[key] || fallback;

const isUrl = (v) => typeof v === "string" && /^https?:\/\/\S+/i.test(v);
const isImageUrl = (v) => isUrl(v) && /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(v);
const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);
const isObjectIdLike = (v) => typeof v === "string" && /^[a-f\d]{24}$/i.test(v);

// On enlève les champs et valeurs inutiles/expirés
const HIDDEN_KEYS = new Set(["_id", "createdAt", "updatedAt", "__v", "experyDate", "expire"]);
const shouldOmitEntry = (k, v) =>
  HIDDEN_KEYS.has(k) || isObjectIdLike(k) || isObjectIdLike(v);

function deepClean(data) {
  if (data == null) return data;
  if (typeof data === "string") return isObjectIdLike(data) ? undefined : data;
  if (typeof data === "number" || typeof data === "boolean") return data;

  if (Array.isArray(data))
    return data?.map(deepClean).filter((v) => v !== undefined);

  if (isPlainObject(data)) {
    const out = {};
    for (const [k, raw] of Object.entries(data)) {
      if (shouldOmitEntry(k, raw)) continue;
      const v = deepClean(raw);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return data;
}

/** Carte spécifique { name, images:[urls] } avec name réduit + grille d’images */
function GalleryCard({ name, images = [] }) {
  const imgUrls = Array.isArray(images) ? images.filter(isUrl) : [];
  return (
    <div className="pp-gcard">
      <div className="pp-gname" title={name || ""}>{name ?? ""}</div>
      {imgUrls?.length > 0 ? (
        <div className="pp-grid-urls">
          {imgUrls?.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer" className="pp-url-cell">
              <img src={u} alt="" className="pp-thumb" />
              <span className="pp-urltext">{u}</span>
            </a>
          ))}
        </div>
      ) : (
        <div className="pp-empty-mini">{__elakayText("t007", "Aucune image")}</div>
      )}
    </div>
  );
}

function RenderValue({ value, allowedKeys=[], key_off = true  }) {
  if (value == null) return <em className="pp-dim">{__elakayText("t002", "null")}</em>;
  if (typeof value === "boolean") return value ? "✅" : "❌";
  if (typeof value === "number") return value;

  // Chaînes: toujours <img> devant chaque URL (image ou non)
  if (typeof value === "string") {
    if (isUrl(value)) {
      return (
        <a href={value} target="_blank" rel="noreferrer" className="pp-link-img">
          <img
            // src={isImageUrl(value) ? value : "https://cdn-icons-png.flaticon.com/512/545/545682.png"}
            src={value}
            alt=""
            className="pp-thumb"
          />
          <span className="pp-urltext">{value}</span>
        </a>
      );
    }
    return value;
  }

  // Tableaux
  if (Array.isArray(value)) {
    if (value?.length === 0) return <span className="pp-dim">[]</span>;

    // Cas tableau d’objets { name, images }
    const allObjs = value.every(isPlainObject);
    const looksLikeGalleries = allObjs && value.every(
      (o) => ("name" in o) && ("images" in o) && Array.isArray(o.images)
    );
    if (looksLikeGalleries) {
      return (
        <div className="pp-glist">
          {value?.map((g, i) => (
            <GalleryCard key={i} name={g.name} images={g.images} />
          ))}
        </div>
      );
    }

    // Tableau d’objets génériques → sous-table (name réduit via CSS th/td)
    if (allObjs) {
      const headerKeys = Array.from(
        new Set(
          value.flatMap((o) => Object.keys(o).filter((k) =>  !HIDDEN_KEYS.has(k) && !isObjectIdLike(k)))
        )
      );
      if (headerKeys?.length === 0) return <span className="pp-dim">[{{}}]</span>;
      return (
        <div className="pp-subtable-wrap">
          <table className="pp-subtable">
            <thead>
              <tr>
                {headerKeys?.map((k) => (
                  <th key={k} className={k === "name" ? "pp-name-col" : undefined}>
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {value?.map((row, i) => (
                <tr key={i}>
                  {headerKeys?.map((k) => (
                    <td key={k} className={k === "name" ? "pp-name-col" : undefined}>
                      <RenderValue value={row[k]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Tableau de chaînes: si toutes sont URL → grille d’images/liens
    const allUrls = value.every((v) => typeof v === "string" && isUrl(v));
    if (allUrls) {
      return (
        <div className="pp-grid-urls">
          {value?.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer" className="pp-url-cell">
              <img
                src={u}
                alt=""
                className="pp-thumb"
              />
              <span className="pp-urltext">{u}</span>
            </a>
          ))}
        </div>
      );
    }

    // Mixte / autres
    return (
      <ul className="pp-list">
        {value?.map((v, i) => (
          <li key={i}><RenderValue value={v} /></li>
        ))}
      </ul>
    );
  }

  // Objets « simples »: key/value (name rendu petit via CSS si clé == 'name')
  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([k, v]) => !shouldOmitEntry(k, v));
    if (entries?.length === 0) return <span className="pp-dim">{`{}`}</span>;
    return (
      <table className="pp-subtable">
        <tbody>
          {entries?.map(([k, v]) => (
            <tr key={k}>
             {key_off && <th className={k === "name" ? "pp-name-col" : undefined}>{k}</th>}
              <td className={k === "name" ? "pp-name-col" : undefined}>
                <RenderValue value={v} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return JSON.stringify(value);
}

export default function PriProductTable({data}) {
  const raw = data?.PriProduct ?? data;
  if (!raw || typeof raw !== "object") return <div>{__elakayText("t008", "Aucune donnée trouvée.")}</div>;
  const PriProduct = deepClean(raw);
  return (
    <div className="pp-container">
      <h2 className="pp-title">{__elakayText("t009", "🛍️ PriProduct")}</h2>
      <div className="pp-wrapper">
        <table className="pp-main">
          <thead>
            <tr>
              <th>{__elakayText("t005", "Key")}</th>
              <th>{__elakayText("t006", "Value")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(PriProduct)?.map(([key, value]) => (
              <tr key={key}>
                <th className={key === "name" ? "pp-name-col" : undefined}>{key}</th>
                <td className={key === "name" ? "pp-name-col" : undefined} >
                  <RenderValue value={value} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        /* Container: prend la largeur du parent (responsive), min 800px via wrapper */
        .pp-container {
          display: flex; flex-direction: column; align-items: center;
          padding: 20px; background: #fff; border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08); width: 100%;
        }
        .pp-wrapper { width: 100%; min-width: 800px; overflow-x: auto; }
        .pp-title { text-align: center; margin-bottom: 16px; font-size: 20px; color: #222; }

        /* Table principale */
        .pp-main { width: 100%; border-collapse: collapse; font-family: system-ui, sans-serif; font-size: 14px; }
        .pp-main th {
          background: #f4f6f8; font-weight: 600; color: #333; text-align: left;
          border: 1px solid #e0e0e0; padding: 9px; width: 200px; white-space: nowrap;
        }
        .pp-main td { border: 1px solid #e0e0e0; padding: 9px; vertical-align: top; background: #fff; }

        /* Sous-tables */
        .pp-subtable-wrap { overflow: auto; }
        .pp-subtable { width: 100%; border-collapse: collapse; font-size: 13px; margin: 6px 0; background: #fafafa; }
        .pp-subtable th { background: #f0f2f5; padding: 6px; border: 1px solid #ddd; text-align: left; font-weight: 600; white-space: nowrap; }
        .pp-subtable td { border: 1px solid #ddd; padding: 6px; vertical-align: top; }

        /* Réduction de la taille pour les "name" + ellipsis */
        .pp-name-col { font-size: 12px; color: #334155; }
        .pp-gname {
          font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 6px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
        }

        /* Grille responsive des URLs (chaque URL = img + texte cliquable) */
        .pp-grid-urls {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px;
        }
        .pp-url-cell { display: flex; align-items: center; gap: 8px; text-decoration: none; }
        .pp-url-cell:hover .pp-urltext { text-decoration: underline; }
        .pp-urltext { color: #0a66c2; word-break: break-all; font-size: 12px; line-height: 1.3; }

        .pp-link-img {
          display: inline-flex; align-items: center; gap: 8px; color: #0a66c2; text-decoration: none; word-break: break-all;
        }
        .pp-link-img:hover .pp-urltext { text-decoration: underline; }

        /* Vignettes toujours devant chaque URL */
        .pp-thumb {
          width: 40px; height: 40px; object-fit: cover; border-radius: 4px; border: 1px solid #e5e7eb; flex-shrink: 0;
        }

        /* Liste par défaut */
        .pp-list { margin: 0; padding-left: 18px; }
        .pp-dim { color: #94a3b8; }
        .pp-empty-mini { color: #64748b; font-size: 12px; }

        /* Cartes de galeries */
        .pp-glist { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
        .pp-gcard { border: 1px dashed #e5e7eb; padding: 10px; border-radius: 8px; background: #fcfcfd; }

        /* Responsive */
        @media (max-width: 900px) {
          .pp-wrapper { min-width: 600px; }
          .pp-main th { width: 170px; font-size: 13px; }
          .pp-thumb { width: 30px; height: 30px; }
          .pp-grid-urls { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
          .pp-glist { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
        }
      `}</style>
    </div>
  );
}
